import type { ReactNode } from 'react'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import { ResizableDrawer } from '@/components/ui/resizable-drawer.organism'

import { PromptTemplateDrawerContent } from './prompt-template-drawer-content.molecule'
import type { PromptTemplateDrawerFormSchemaType } from './prompt-template-drawer-form.schema'
import { usePromptTemplateDrawer } from './use-prompt-template-drawer.hook'

interface PromptTemplateDrawerProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  template: PromptTemplate | null
  onSave: (values: PromptTemplateDrawerFormSchemaType) => void
}

/**
 * Drawer for creating or editing a prompt template.
 * Uses the unified ResizableDrawer for desktop/mobile rendering with animations.
 */
export const PromptTemplateDrawer = ({
  isOpen,
  onOpenChange,
  template,
  onSave,
}: PromptTemplateDrawerProps): ReactNode => {
  const { form, isEditing, handleSubmit, handleCancel } = usePromptTemplateDrawer({
    template,
    onSave,
    onClose: () => onOpenChange(false),
  })

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Edit Prompt Template' : 'Create Prompt Template'}
      description="Define a reusable prompt template"
      className="prompt-template-drawer"
    >
      <PromptTemplateDrawerContent
        form={form}
        isEditing={isEditing}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    </ResizableDrawer>
  )
}

PromptTemplateDrawer.displayName = 'PromptTemplateDrawer'
